import {
  ApolloError,
  type BaseMutationOptions,
  type OperationVariables,
  QueryLazyOptions,
  TypedDocumentNode,
  useMutation
} from '@apollo/client';
import { FetchResult } from '@apollo/client/link/core';
import { MutationHookOptions } from '@apollo/client/react/types/types';
import { useApolloErrorSnackbar } from 'utils/hooks/useApolloErrorSnackbar';

interface UseTypesMutationOutput<TData, TVariables> {
  loading: boolean;
  data?: TData;
  error?: ApolloError;
  reset: () => void;
  set: (options?: QueryLazyOptions<TVariables>) => Promise<FetchResult<TData>>;
}

// Small util that wrap you typed query and returns all fields that useMutation gives plus getter function that return a Promise with Data.
// Error handling attached.
// Same API you can find in useTypedQuery
export function useTypedMutation<TData, TVariables>(
  query: TypedDocumentNode<TData, TVariables>,
  options?: Omit<MutationHookOptions<TData, TVariables>, 'onError'> & {
    onError?: (
      error: ApolloError,
      clientOptions?: BaseMutationOptions<TVariables> & { variables?: TVariables }
    ) => void;
  }
) {
  const onError = useApolloErrorSnackbar();
  const [set, { loading, data, error, reset }] = useMutation<any, OperationVariables>(query, {
    errorPolicy: 'none',
    onError,
    ...options
  } as BaseMutationOptions);

  return {
    loading,
    data,
    error,
    set,
    reset
  } as UseTypesMutationOutput<TData, TVariables>;
}
